import { useCallback } from 'react';
import {
  buildBatchParams,
  buildQueryString,
  formatCopyLinks,
} from '../utils/certificados.utils';
import {
  CERTIFICADO_ENDPOINT,
  CERTIFICADOS_ENDPOINT,
} from '../../../hooks/constants';
import { avaliableActions } from '../../../models/actions';

export const useCertificadosBatch = ({
  backendGet,
  backendPut,
  showNotification,
  fetchData,
  deleteSelectedItems,
  searchText,
  checkFilter,
  listModel,
  clienteCpf,
  setCopyLoading,
}) => {
  const getBaseContext = useCallback(
    () => ({ searchText, checkFilter, listModel }),
    [searchText, checkFilter, listModel],
  );

  const handleDeleteSelectedItemsAction = useCallback(
    async (action, selectedItems, isAllSelected, onSuccess) => {
      if (action !== avaliableActions.confirmar) return true;

      const params = buildBatchParams({
        selectedItems,
        isAllSelected,
        ...getBaseContext(),
      });

      await deleteSelectedItems(
        params,
        onSuccess,
        () => {},
        clienteCpf,
        searchText,
      );

      return true;
    },
    [clienteCpf, deleteSelectedItems, getBaseContext, searchText],
  );

  const handleChangeValuesCertificado = useCallback(
    // eslint-disable-next-line
    (selectedItems, isAllSelected, onSuccess, isTemplate) =>
      async (newValue) => {
        const ids = isAllSelected ? null : selectedItems.map(({ id }) => id);
        const query = buildQueryString(getBaseContext());

        const params = {
          loteDTO: { ids, todos: isAllSelected },
          nome: !isTemplate ? newValue : null,
          tipo: isTemplate ? newValue : null,
        };

        try {
          await backendPut(`${CERTIFICADO_ENDPOINT}/lote${query}`, params);
          onSuccess({ resetSelection: true });
          fetchData({ sort: { field: 'dataConclusao', type: 'DESC' } });
          return { success: true };
        } catch (e) {
          console.error(e);
          return {
            success: false,
            message: e?.message ?? 'Não foi possível alterar os certificados.',
          };
        }
      },
    [backendPut, fetchData, getBaseContext],
  );

  const handleSendInLot = useCallback(
    (selectedItems, isAllSelected, onSuccess, func, setSendLoading) => {
      const params = buildBatchParams({
        selectedItems,
        isAllSelected,
        ...getBaseContext(),
      });

      setSendLoading(true);
      func(params, onSuccess, setSendLoading);
    },
    [getBaseContext],
  );

  const copyLinks = useCallback(
    (items, isAllSelected, reloadData) => {
      const text = formatCopyLinks(items, isAllSelected);
      navigator.clipboard.writeText(text);
      reloadData({ resetSelection: true });
      setCopyLoading(false);
      showNotification({
        message: 'Links copiados',
        supportMessage:
          'A lista de links foi copiada e está na área de transferência',
        color: 'confirmation',
        secondsToClose: 5,
      });
    },
    [setCopyLoading, showNotification],
  );

  const handleCopyAll = useCallback(
    (isAllSelected, selectedItems, reloadData) => {
      setCopyLoading(true);

      if (!isAllSelected) {
        copyLinks(selectedItems, false, reloadData);
        return;
      }

      const query = buildQueryString(getBaseContext());
      backendGet(`${CERTIFICADOS_ENDPOINT}/lote/links${query}`)
        .then((data) => copyLinks(data, true, reloadData))
        .catch((error) => {
          showNotification({
            message: 'Não foi possível copiar todos os links!',
            supportMessage: error.message,
            color: 'destructive',
          });
          setCopyLoading(false);
        });
    },
    [backendGet, copyLinks, getBaseContext, setCopyLoading, showNotification],
  );

  return {
    handleDeleteSelectedItemsAction,
    handleChangeValuesCertificado,
    handleSendInLot,
    handleCopyAll,
  };
};
